import React from "react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { FormStack } from "../components/FormStack/FormStack";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from "@chakra-ui/react";
import { CopyText } from "../components/Utilities/CopyText";
import { ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import { PlusIcon } from "../styles/icons";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { ServiceManagementPermissions } from "../enums/ServiceManagementPermissions";
import { entriesKeyValue } from "../util/entriesObject";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { TextField } from "../components/fields/TextField/TextField";
import * as Yup from "yup";

interface AccesssRightKvp {
  key: string;
  keyAppend?: string;
  value: ServiceManagementPermissions;
}

interface IntegrationAccessRightsFormValues {
  ClaimBasedAuthorisation: AccesssRightKvp[];
  UserBasedAuthorisation: AccesssRightKvp[];
}

enum ClaimType {
  Claim,
  User,
}

interface IntegrationAccessRightsFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integrationId: string;
  accessRights?: CherryHubAdminApi.ServiceManagementPermissions | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  integrationId,
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  setFieldValue,
  values,
}: IntegrationAccessRightsFormProps &
  FormikProps<IntegrationAccessRightsFormValues>) => {
  const { data: claimOptions, isLoading: claimOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.SupportedCustomClaim
        ),
      []
    );

  const { data: permissionOptions, isLoading: permissionLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.CloudServiceManagementRole
        ),
      []
    );

  const deleteAccessRight = (claimType: ClaimType, claimIdx: number) => {
    if (claimType == ClaimType.Claim) {
      values.ClaimBasedAuthorisation.splice(claimIdx, 1);
      setFieldValue("ClaimBasedAuthorisation", values.ClaimBasedAuthorisation);
    } else if (claimType == ClaimType.User) {
      values.UserBasedAuthorisation.splice(claimIdx, 1);
      setFieldValue("UserBasedAuthorisation", values.UserBasedAuthorisation);
    }
  };

  return (
    <FormStack>
      <HStack w="100%">
        <Box flexGrow={2} pb={4}>
          <CopyText text={integrationId} hover adjustMargin>
            <Text fontSize="md" color="cherryUi.600">
              {integrationId}
            </Text>
          </CopyText>
          <Text fontSize="xl" color="cherryUi.600">
            <b>Integration Permissions</b>
          </Text>
        </Box>

        <Box>
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !isValid}
            onClick={submitForm}
          >
            Save
          </Button>
        </Box>

        <Box>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      </HStack>

      <Accordion
        w="100%"
        allowToggle
        allowMultiple
        variant="section"
        defaultIndex={[0, 1]}
      >
        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <b>Claim Based Authorisation</b>
                  </Box>
                  {isExpanded ? (
                    <ChevronUpIcon color="white" fontSize="xl" />
                  ) : (
                    <AccordionIcon />
                  )}
                </AccordionButton>
              </h2>
              <AccordionPanel width="100%">
                <VStack width="100%" alignSelf="flex-start" spacing={4} mb={4}>
                  <Button
                    alignSelf="flex-end"
                    mb={4}
                    size="sm"
                    leftIcon={<PlusIcon />}
                    onClick={() => {
                      values.ClaimBasedAuthorisation.push({
                        key: "",
                        value: ServiceManagementPermissions.None,
                      });
                      setFieldValue(
                        "ClaimBasedAuthorisation",
                        values.ClaimBasedAuthorisation
                      );
                    }}
                    colorScheme="cherryButton"
                  >
                    Add Claim
                  </Button>

                  {(values.ClaimBasedAuthorisation.length &&
                    values.ClaimBasedAuthorisation.map(
                      (userClaim, claimIdx) => (
                        <AccessRightsField
                          key={claimIdx}
                          claimType={ClaimType.Claim}
                          claimIdx={claimIdx}
                          claimOptions={claimOptions}
                          claimOptionsLoading={claimOptionsLoading}
                          permissionOptions={permissionOptions}
                          permissionOptionsLoading={permissionLoading}
                          deleteAccessRight={deleteAccessRight}
                        />
                      )
                    )) || <i>Claim Based Authorisation has not been set.</i>}
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>

        <AccordionItem>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <b>User Based Authorisation</b>
                  </Box>
                  {isExpanded ? (
                    <ChevronUpIcon color="white" fontSize="xl" />
                  ) : (
                    <AccordionIcon />
                  )}
                </AccordionButton>
              </h2>
              <AccordionPanel width="100%">
                <VStack width="100%" alignSelf="flex-start" spacing={4} mb={4}>
                  <Button
                    alignSelf="flex-end"
                    mb={4}
                    size="sm"
                    leftIcon={<PlusIcon />}
                    onClick={() => {
                      values.UserBasedAuthorisation.push({
                        key: "",
                        value: ServiceManagementPermissions.None,
                      });
                      setFieldValue(
                        "UserBasedAuthorisation",
                        values.UserBasedAuthorisation
                      );
                    }}
                    colorScheme="cherryButton"
                  >
                    Add User
                  </Button>

                  {(values.UserBasedAuthorisation.length &&
                    values.UserBasedAuthorisation.map((userClaim, claimIdx) => (
                      <AccessRightsField
                        key={claimIdx}
                        claimType={ClaimType.User}
                        claimIdx={claimIdx}
                        claimOptions={claimOptions}
                        claimOptionsLoading={claimOptionsLoading}
                        permissionOptions={permissionOptions}
                        permissionOptionsLoading={permissionLoading}
                        deleteAccessRight={deleteAccessRight}
                      />
                    ))) || <i>User Based Authorisation has not been set.</i>}
                </VStack>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>

      <HStack width="100%" justifyContent="start" spacing="3" pt="4">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const IntegrationAccessRightsForm = withFormik<
  IntegrationAccessRightsFormProps,
  IntegrationAccessRightsFormValues
>({
  mapPropsToValues: ({ accessRights }) => {
    return {
      ClaimBasedAuthorisation:
        (accessRights?.ClaimBasedAuthorisation &&
          entriesKeyValue(accessRights?.ClaimBasedAuthorisation).map(
            ([key, value]) => {
              const keys: string[] = key.split("+");
              return {
                key: keys.length > 1 ? keys[0] : key,
                keyAppend: keys.length > 1 ? keys[1] : undefined,
                value,
              };
            }
          )) ||
        [],
      UserBasedAuthorisation:
        (accessRights?.UserBasedAuthorisation &&
          entriesKeyValue(accessRights?.UserBasedAuthorisation).map(
            ([key, value]) => {
              return {
                key,
                value,
              };
            }
          )) ||
        [],
    };
  },
  handleSubmit: async (values, { props, setFieldError }) => {
    let claimBasedUpdate: {
      [key: string]: ServiceManagementPermissions;
    } = {};
    values.ClaimBasedAuthorisation.forEach((claim) => {
      let key = claim.key;
      if (claim.keyAppend) {
        key += `+${claim.keyAppend}`;
      }
      claimBasedUpdate[key] = claim.value;
    });

    let userBasedUpdate: {
      [key: string]: ServiceManagementPermissions;
    } = {};
    values.UserBasedAuthorisation.forEach((claim) => {
      userBasedUpdate[claim.key] = claim.value;
    });

    let result = await props.apiClient.updateIntegrationPermissions(
      props.businessId,
      props.integrationId,
      {
        ClaimBasedAuthorisation: claimBasedUpdate,
        UserBasedAuthorisation: userBasedUpdate,
      }
    );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess();
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
  validate: (values) => {
    let errors: FormikErrors<IntegrationAccessRightsFormValues> = {};

    return errors;
  },
  validationSchema: Yup.object().shape({
    ClaimBasedAuthorisation: Yup.array().of(
      Yup.object().shape({
        key: Yup.string().required("Claim Name is a required field"),
        keyAppend: Yup.string().required("Claim Value is a required field"),
        value: Yup.string().required("Access Level is a required field"),
      })
    ),
    UserBasedAuthorisation: Yup.array().of(
      Yup.object().shape({
        key: Yup.string()
          .email()
          .required("Email Address is a required field."),
        value: Yup.string().required("Access Level is a required field"),
      })
    ),
  }),
})(InnerForm);

interface AccessRightsFieldProps {
  claimType: ClaimType;
  claimOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  claimOptionsLoading: boolean;
  permissionOptions: CherryHubAdminApi.EnumConfigurationItem[] | null;
  permissionOptionsLoading: boolean;
  claimIdx: number;
  deleteAccessRight: (claimType: ClaimType, claimIdx: number) => void;
}

const AccessRightsField = ({
  claimType,
  claimOptions,
  claimOptionsLoading,
  permissionOptions,
  permissionOptionsLoading,
  claimIdx,
  deleteAccessRight,
}: AccessRightsFieldProps) => {
  const formName =
    claimType == ClaimType.Claim
      ? "ClaimBasedAuthorisation"
      : "UserBasedAuthorisation";
  return (
    <HStack w="100%" alignItems="flex-start">
      {claimType == ClaimType.Claim && (
        <Box flex={2}>
          <ChakraReactSelectField
            label={`Claim Name ${claimIdx + 1}`}
            name={`${formName}[${claimIdx}].key`}
            selectedOptionColor="blue"
            isLoading={claimOptionsLoading}
            options={
              claimOptions?.map((provider) => {
                return {
                  label: provider.Value!,
                  value: provider.Key!,
                };
              }) || []
            }
            className="relative-listbox"
          />
        </Box>
      )}
      <Box flex={3}>
        <TextField
          label={`Claim Value ${claimIdx + 1}`}
          name={`${formName}[${claimIdx}].${
            claimType == ClaimType.Claim ? "keyAppend" : "key"
          }`}
        />
      </Box>
      <Box flex={2}>
        <ChakraReactSelectField
          label={`Access Level ${claimIdx + 1}`}
          name={`${formName}[${claimIdx}].value`}
          selectedOptionColor="blue"
          isLoading={permissionOptionsLoading}
          options={
            permissionOptions?.map((permission) => {
              return {
                label: permission.Value!,
                value: permission.Key!,
              };
            }) || []
          }
          className="relative-listbox"
        />
      </Box>
      <Box pt={6}>
        <IconButton
          aria-label={`Delete claim.`}
          title={`Delete claim.`}
          icon={<DeleteIcon />}
          size="sm"
          onClick={() => deleteAccessRight(claimType, claimIdx)}
        />
      </Box>
    </HStack>
  );
};
